import React, { useEffect, useRef, useState } from "react";
import { AD_TARGET_TYPES, GENDERS, DEVICES, AGE_MIN, AGE_MAX, formatInterestLabel, getCurrentUserId } from "./adsConstants";
import { getInterestList, getMySellerId, getMyProducts } from "../../hook/useAdsApi";

const PillGroup = ({ options, value, onChange }) => (
  <div className="flex gap-3 flex-wrap">
    {options.map((opt) => {
      const selected = value.toLowerCase() === opt.toLowerCase();
      return (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt.toLowerCase())}
          className={`px-5 py-2 rounded-full text-sm font-semibold ${
            selected ? "bg-[#095228] text-white" : "border border-[#c3c5d9] text-[#434656]"
          }`}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`w-14 h-7 rounded-full relative shrink-0 overflow-hidden transition-colors focus:outline-none ${
      checked ? "bg-[#095228]" : "bg-[#c3c5d9]"
    } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
  >
    <span
      className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-all ${
        checked ? "left-8" : "left-1"
      }`}
    />
  </button>
);

export default function CreateAdSet({ formData, updateFormData, onNext, onBack, continueLabel = "Continue", submitting = false, adAccount }) {
  // client-api rejects "Connection to Seller" targeting (seller_relationship.followers)
  // unless the ad account has a seller_id - i.e. is a Marketplace Seller account.
  const hasSellerAccount = !!adAccount?.seller_id;
  const [availableInterests, setAvailableInterests] = useState([]);
  const [interestsError, setInterestsError] = useState(null);
  const [interestDropdownOpen, setInterestDropdownOpen] = useState(false);
  const interestDropdownRef = useRef(null);
  const [myProducts, setMyProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState(null);

  const canContinue = formData.adSetName.trim() && formData.targetType && formData.targetId;

  useEffect(() => {
    if (formData.targetType === "profile" && !formData.targetId) {
      updateFormData({ targetId: getCurrentUserId() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Disabling the toggle above only hides it visually - the underlying value has to be
  // force-reset too, otherwise a stale `true` from before the account was known to be
  // ineligible still gets submitted and trips the same "requires a marketplace seller ad
  // account" error server-side, even though the toggle displays as off.
  useEffect(() => {
    if (!hasSellerAccount && formData.connectToSeller) {
      updateFormData({ connectToSeller: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSellerAccount]);

  // Product picker: /buyer/get-products has no auto-scoping, so this resolves the caller's
  // own seller_id first, then filters by it - fetched once, on first switching to "product".
  useEffect(() => {
    if (formData.targetType !== "product" || myProducts.length > 0 || productsLoading) return;
    setProductsLoading(true);
    getMySellerId()
      .then((sellerId) => (sellerId ? getMyProducts(sellerId) : []))
      .then(setMyProducts)
      .catch(() => setProductsError("Couldn't load your products. Try again later."))
      .finally(() => setProductsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.targetType]);

  // interest_ids on the backend must be real, existing Interest row ids (validated) - not
  // free text, so this fetches the real list to select from instead of letting the seller type.
  useEffect(() => {
    getInterestList()
      .then(setAvailableInterests)
      .catch(() => setInterestsError("Couldn't load interests. Try again later."));
  }, []);

  // Close the dropdown on an outside click, same as any standard multiselect.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (interestDropdownRef.current && !interestDropdownRef.current.contains(e.target)) {
        setInterestDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectTargetType = (value) => {
    // "profile" targets the logged-in user's own id automatically; product/live need a real
    // target_id typed in - client-api only checks that the id exists, it doesn't require a
    // picker UI.
    updateFormData({ targetType: value, targetId: value === "profile" ? getCurrentUserId() : "" });
  };

  const toggleInterest = (interest) => {
    const isSelected = formData.interests.some((i) => i.id === interest.id);
    updateFormData({
      interests: isSelected
        ? formData.interests.filter((i) => i.id !== interest.id)
        : [...formData.interests, { id: interest.id, label: formatInterestLabel(interest.category) }],
    });
  };

  return (
    <div>
      {/* Colors/typography carried over from Figma's Step 1 & account screens (read-only
          access) - Ad Set itself isn't in the Figma file, so this matches the established
          design language rather than an exact per-field spec. */}
      <label className="block text-sm font-semibold text-black mb-1">Ad Set Name</label>
      <input
        type="text"
        value={formData.adSetName}
        onChange={(e) => updateFormData({ adSetName: e.target.value })}
        placeholder="Enter Ad Set Name"
        className="w-full border border-[#c3c5d9] rounded-xl px-4 py-3 mb-6"
      />

      <label className="block text-sm font-semibold text-black mb-2">Target Type</label>
      <div className="flex gap-3 mb-2">
        {AD_TARGET_TYPES.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => selectTargetType(t.value)}
            className={`px-5 py-2 rounded-full text-sm font-semibold ${
              formData.targetType === t.value
                ? "bg-[#095228] text-white"
                : "border border-[#c3c5d9] text-[#434656]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {formData.targetType === "product" && (
        <>
          {productsError && <p className="text-red-500 text-xs mb-2">{productsError}</p>}
          <select
            value={formData.targetId}
            onChange={(e) => updateFormData({ targetId: e.target.value })}
            className="w-full border border-[#c3c5d9] rounded-xl px-4 py-3 mb-2 text-sm"
          >
            <option value="">{productsLoading ? "Loading your products..." : "Select a product"}</option>
            {myProducts.map((p) => (
              <option key={p.id} value={p.id}>{p.product_name}</option>
            ))}
          </select>
          <p className="text-xs text-[#747474] mb-10">
            {!productsLoading && !productsError && myProducts.length === 0
              ? "You don't have any product listings yet."
              : "Choose one of your own product listings."}
          </p>
        </>
      )}
      {formData.targetType === "live" && (
        <>
          {/* No listing endpoint exposes the plain numeric LiveStream id needed here (only a
              separate stream_id string used elsewhere in the live module) - entered directly
              until that's exposed. */}
          <input
            type="number"
            value={formData.targetId}
            onChange={(e) => updateFormData({ targetId: e.target.value })}
            placeholder="Enter Live Stream ID"
            className="w-full border border-[#c3c5d9] rounded-xl px-4 py-3 mb-2"
          />
          <p className="text-xs text-[#747474] mb-10">The numeric ID of one of your own live streams.</p>
        </>
      )}
      {formData.targetType === "profile" && <div className="mb-8" />}

      <label className="block text-sm font-semibold text-black mb-2">Gender</label>
      <div className="mb-6">
        <PillGroup options={GENDERS} value={formData.gender} onChange={(v) => updateFormData({ gender: v })} />
      </div>

      <label className="block text-sm font-semibold text-black mb-2">Audience Age</label>
      <div className="relative h-8 mb-2">
        <div className="absolute top-3 left-0 right-0 h-1 bg-[#d9d9d9] rounded-full" />
        <div
          className="absolute top-3 h-1 bg-[#095228] rounded-full"
          style={{
            left: `${((formData.minAge - AGE_MIN) / (AGE_MAX - AGE_MIN)) * 100}%`,
            right: `${100 - ((formData.maxAge - AGE_MIN) / (AGE_MAX - AGE_MIN)) * 100}%`,
          }}
        />
        <input
          type="range"
          min={AGE_MIN}
          max={AGE_MAX}
          value={formData.minAge}
          onChange={(e) => updateFormData({ minAge: Math.min(Number(e.target.value), formData.maxAge - 1) })}
          className="absolute w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#095228] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow"
        />
        <input
          type="range"
          min={AGE_MIN}
          max={AGE_MAX}
          value={formData.maxAge}
          onChange={(e) => updateFormData({ maxAge: Math.max(Number(e.target.value), formData.minAge + 1) })}
          className="absolute w-full appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#095228] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow"
        />
      </div>
      <div className="flex justify-between text-xs text-[#747474] mb-6">
        <span>{AGE_MIN}</span>
        <span>{formData.minAge} - {formData.maxAge}</span>
        <span>{AGE_MAX}</span>
      </div>

      <label className="block text-sm font-semibold text-black mb-2">Interests</label>
      {interestsError && <p className="text-red-500 text-xs mb-2">{interestsError}</p>}
      <div className="relative mb-3" ref={interestDropdownRef}>
        <button
          type="button"
          onClick={() => setInterestDropdownOpen((open) => !open)}
          className="w-full text-left border border-[#c3c5d9] rounded-xl px-4 py-3 text-sm text-[#9a9a9a] flex items-center justify-between"
        >
          Add Interests
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9a9a9a" strokeWidth="2.5" style={{ transform: interestDropdownOpen ? "rotate(180deg)" : "none" }}>
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {interestDropdownOpen && (
          <div className="absolute z-10 top-full mt-1 w-full bg-white border border-[#c3c5d9] rounded-xl shadow-lg max-h-56 overflow-y-auto">
            {availableInterests.map((interest) => {
              const selected = formData.interests.some((i) => i.id === interest.id);
              return (
                <button
                  key={interest.id}
                  type="button"
                  onClick={() => toggleInterest(interest)}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-left hover:bg-gray-50"
                >
                  {formatInterestLabel(interest.category)}
                  {selected && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#095228" strokeWidth="3"><path d="M5 13l4 4L19 7" /></svg>
                  )}
                </button>
              );
            })}
            {!availableInterests.length && (
              <p className="px-4 py-2.5 text-xs text-[#9a9a9a]">
                {interestsError || "No interests available yet."}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-2 flex-wrap mb-6">
        {formData.interests.map((interest) => (
          <span
            key={interest.id}
            className="flex items-center gap-2 border border-black rounded-full pl-4 pr-1.5 py-1.5 text-sm font-bold"
          >
            {interest.label}
            <button
              type="button"
              onClick={() => toggleInterest(interest)}
              className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center shrink-0"
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </span>
        ))}
      </div>

      <label className="block text-sm font-semibold text-black mb-2">Device &amp; Tech</label>
      <div className="mb-6">
        <PillGroup options={DEVICES} value={formData.devices} onChange={(v) => updateFormData({ devices: v })} />
      </div>

      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold text-black">Connection to Seller</span>
        <Toggle
          checked={hasSellerAccount && formData.connectToSeller}
          onChange={(v) => updateFormData({ connectToSeller: v })}
          disabled={!hasSellerAccount}
        />
      </div>
      {!hasSellerAccount && (
        <p className="text-xs text-[#747474] mb-3">Only available for Marketplace Seller ad accounts.</p>
      )}
      {hasSellerAccount && <div className="mb-3" />}
      <div className="flex items-center justify-between mb-8">
        <span className="text-sm font-semibold text-black">Previous Brand Buyers</span>
        <Toggle checked={formData.previousBrandBuyers} onChange={(v) => updateFormData({ previousBrandBuyers: v })} />
      </div>

      <div className="flex gap-3">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="border border-[#c3c5d9] text-[#434656] font-medium text-lg px-8 py-3 rounded-full"
          >
            Back
          </button>
        )}
        <button
          type="button"
          disabled={!canContinue || submitting}
          onClick={onNext}
          className="bg-[#095228] border border-[#39b54a] text-[#f9e9ea] font-medium text-lg px-10 py-3 rounded-full shadow-[0_0_6px_rgba(59,182,76,0.53)] disabled:opacity-50"
        >
          {submitting ? "Saving..." : continueLabel}
        </button>
      </div>
    </div>
  );
}
